import type { CoasterProject, TrackBehavior, Vec3 } from '../schema';

const FIXTURE_TIME = '2026-07-15T00:00:00.000Z';

type FixtureNode = { id: string; position: Vec3; bankDegrees?: number };

function makeProject(
  id: string,
  title: string,
  nodes: FixtureNode[],
  behaviors: TrackBehavior[],
  closed = true,
): CoasterProject {
  const trackNodes = nodes.map((node) => ({
    id: node.id,
    position: node.position,
    bankDegrees: node.bankDegrees ?? 0,
  }));
  const sectionCount = closed ? trackNodes.length : Math.max(0, trackNodes.length - 1);
  const sections = Array.from({ length: sectionCount }, (_, index) => ({
    id: `section:${id}:${index}`,
    fromNodeId: trackNodes[index]!.id,
    toNodeId: trackNodes[(index + 1) % trackNodes.length]!.id,
    behavior: behaviors[index] ?? ({ kind: 'normal' } as const),
  }));
  return {
    schemaVersion: 2,
    projectId: id,
    revisionId: `${id}:fixture-v1`,
    coasterFamilyId: 'steel.standard',
    metadata: {
      title,
      description: 'Deterministic Buncha Coasters fixture',
      createdAt: FIXTURE_TIME,
      updatedAt: FIXTURE_TIME,
      creator: { kind: 'local', id: null, displayName: 'Fixture creator' },
      tags: ['fixture'],
    },
    plot: {
      shape: 'rectangle',
      sizeMeters: { x: 140, z: 100 },
      origin: { x: 0, y: 0, z: 0 },
      groundElevationMeters: 0,
    },
    track: { splineKind: 'cubic_bspline', degree: 3, closed, nodes: trackNodes, sections },
    train: {
      familyId: 'steel.standard',
      styleId: 'train.standard',
      carCount: 5,
      carSpacingMeters: 2.4,
      emptyMassKg: 4_000,
      capacityPerCar: 4,
    },
    appearance: {
      environmentThemeId: 'park_model',
      trackColor: '#2457b2',
      supportColor: '#343a40',
      trainColor: '#d94a38',
      stationColor: '#d8d2c5',
      trackMaterialId: 'painted_steel',
      supportMaterialId: 'painted_steel',
      trainMaterialId: 'painted_steel',
    },
    supports: { mode: 'automatic', suppressedSectionIds: [], manual: [] },
    scenery: [],
    highlights: sections.length
      ? [
          {
            id: `highlight:${id}:best`,
            kind: 'creator',
            sectionId: sections[Math.min(1, sections.length - 1)]!.id,
            distanceAlongSection: 0.5,
            label: 'Best moment',
          },
        ]
      : [],
    permissions: { visibility: 'private', allowReadOnlyShare: false, allowRemix: false },
    validation: { status: 'not_run', lastRunAt: null, validatorVersion: 'unvalidated', issues: [] },
    heroCamera: {
      position: { x: 110, y: 90, z: 110 },
      target: { x: 0, y: 15, z: 0 },
      verticalFovDegrees: 45,
      nearMeters: 0.1,
      farMeters: 1_000,
      safeMargin: 0.08,
    },
    lineage: null,
  };
}

export const blankCoasterFixture = makeProject('fixture.blank', 'Blank Plot', [], [], false);

export const ovalCoasterFixture = makeProject(
  'fixture.oval',
  'Simple Oval',
  [
    { id: 'node:oval:0', position: { x: -35, y: 4, z: -18 } },
    { id: 'node:oval:1', position: { x: 35, y: 4, z: -18 }, bankDegrees: 15 },
    { id: 'node:oval:2', position: { x: 35, y: 4, z: 18 }, bankDegrees: 15 },
    { id: 'node:oval:3', position: { x: -35, y: 4, z: 18 } },
  ],
  [{ kind: 'station', holdSeconds: 2, dispatchSpeedMps: 2 }],
);

export const liftDropCoasterFixture = makeProject(
  'fixture.lift-drop',
  'Lift and Drop',
  [
    { id: 'node:lift:0', position: { x: -45, y: 4, z: -18 } },
    { id: 'node:lift:1', position: { x: -10, y: 4, z: -18 } },
    { id: 'node:lift:2', position: { x: 15, y: 45, z: -10 } },
    { id: 'node:lift:3', position: { x: 38, y: 5, z: 0 } },
    { id: 'node:lift:4', position: { x: 12, y: 8, z: 25 }, bankDegrees: 28 },
    { id: 'node:lift:5', position: { x: -35, y: 4, z: 18 }, bankDegrees: 18 },
  ],
  [
    { kind: 'lift', targetSpeedMps: 3.5 },
    { kind: 'lift', targetSpeedMps: 3.5 },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'brake', targetSpeedMps: 6, decelerationMps2: 4 },
  ],
);

export const systemsCircuitCoasterFixture = makeProject(
  'fixture.systems-circuit',
  'Systems Circuit',
  [
    { id: 'node:systems:0', position: { x: -50, y: 4, z: -8 } },
    { id: 'node:systems:1', position: { x: -45, y: 4, z: -20 } },
    { id: 'node:systems:2', position: { x: -28, y: 4, z: -24 } },
    { id: 'node:systems:3', position: { x: -8, y: 10, z: -24 } },
    { id: 'node:systems:4', position: { x: 12, y: 42, z: -17 } },
    { id: 'node:systems:5', position: { x: 39, y: 4, z: -8 }, bankDegrees: 10 },
    { id: 'node:systems:6', position: { x: 45, y: 4, z: 7 }, bankDegrees: 16 },
    { id: 'node:systems:7', position: { x: 44, y: 4, z: 22 }, bankDegrees: 20 },
    { id: 'node:systems:8', position: { x: 24, y: 4, z: 34 }, bankDegrees: 22 },
    { id: 'node:systems:9', position: { x: 2, y: 5, z: 37 }, bankDegrees: 20 },
    { id: 'node:systems:10', position: { x: -22, y: 11, z: 31 }, bankDegrees: 16 },
    { id: 'node:systems:11', position: { x: -40, y: 8, z: 21 }, bankDegrees: 12 },
    { id: 'node:systems:12', position: { x: -50, y: 4, z: 10 }, bankDegrees: 6 },
    { id: 'node:systems:13', position: { x: -53, y: 4, z: 1 } },
  ],
  [
    { kind: 'station', holdSeconds: 1.5, dispatchSpeedMps: 2.5 },
    { kind: 'station', holdSeconds: 1.5, dispatchSpeedMps: 2.5 },
    { kind: 'lift', targetSpeedMps: 6 },
    { kind: 'lift', targetSpeedMps: 6 },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'launch', targetSpeedMps: 20, accelerationMps2: 7 },
    { kind: 'launch', targetSpeedMps: 20, accelerationMps2: 7 },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'brake', targetSpeedMps: 7, decelerationMps2: 4 },
    { kind: 'brake', targetSpeedMps: 4, decelerationMps2: 4 },
    { kind: 'station', holdSeconds: 1.5, dispatchSpeedMps: 2.5 },
  ],
);

export const stallHillCoasterFixture = makeProject(
  'fixture.stall-hill',
  'Stall Test',
  [
    { id: 'node:stall:0', position: { x: -25, y: 4, z: -12 } },
    { id: 'node:stall:1', position: { x: -5, y: 4, z: -12 } },
    { id: 'node:stall:2', position: { x: 8, y: 16, z: -7 } },
    { id: 'node:stall:3', position: { x: 14, y: 65, z: 0 } },
    { id: 'node:stall:4', position: { x: 2, y: 10, z: 18 }, bankDegrees: 28 },
    { id: 'node:stall:5', position: { x: -22, y: 4, z: 12 }, bankDegrees: 18 },
  ],
  [
    { kind: 'station', holdSeconds: 0.1, dispatchSpeedMps: 4 },
    { kind: 'lift', targetSpeedMps: 5 },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'brake', targetSpeedMps: 6, decelerationMps2: 4 },
    { kind: 'normal' },
  ],
);

export const loopCoasterFixture = makeProject(
  'fixture.loop',
  'Vertical Loop',
  [
    { id: 'node:loop:0', position: { x: -45, y: 4, z: 0 } },
    { id: 'node:loop:1', position: { x: -15, y: 4, z: 0 } },
    { id: 'node:loop:2', position: { x: 0, y: 20, z: 0 }, bankDegrees: 45 },
    { id: 'node:loop:3', position: { x: 0, y: 45, z: 0 }, bankDegrees: 180 },
    { id: 'node:loop:4', position: { x: 15, y: 20, z: 0 }, bankDegrees: -45 },
    { id: 'node:loop:5', position: { x: 35, y: 4, z: 8 } },
    { id: 'node:loop:6', position: { x: 15, y: 4, z: 30 }, bankDegrees: 20 },
    { id: 'node:loop:7', position: { x: -35, y: 4, z: 25 }, bankDegrees: 20 },
  ],
  [
    { kind: 'station', holdSeconds: 2, dispatchSpeedMps: 2 },
    { kind: 'launch', targetSpeedMps: 24, accelerationMps2: 8 },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'normal' },
    { kind: 'brake', targetSpeedMps: 6, decelerationMps2: 4 },
  ],
);

export const invalidCoasterFixture: unknown = {
  ...ovalCoasterFixture,
  track: {
    ...ovalCoasterFixture.track,
    nodes: [
      ovalCoasterFixture.track.nodes[0],
      {
        ...ovalCoasterFixture.track.nodes[1],
        id: ovalCoasterFixture.track.nodes[0]!.id,
        position: { x: Number.NaN, y: 4, z: 0 },
      },
    ],
    sections: [{ ...ovalCoasterFixture.track.sections[0], toNodeId: 'node:missing' }],
  },
};

export const validCoasterFixtures = [
  blankCoasterFixture,
  ovalCoasterFixture,
  liftDropCoasterFixture,
  systemsCircuitCoasterFixture,
  stallHillCoasterFixture,
  loopCoasterFixture,
] as const;
